import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Btn } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import ProDocuments from "@/pages/ProDocuments";

export default function DoctorOnboarding() {
  const { user, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [checking, setChecking] = useState(false);

  // Pick up Admin verification automatically (every 30s) — no sign-out needed.
  useEffect(() => {
    refreshUser();
    const t = setInterval(() => { refreshUser(); }, 30000);
    return () => clearInterval(t);
  }, [refreshUser]);
  useEffect(() => {
    if (user?.credential_status === "Verified") { nav("/dashboard", { replace: true }); }
  }, [user?.credential_status, nav]);

  const check = async () => {
    setChecking(true);
    const u = await refreshUser();
    setChecking(false);
    if (u && u.credential_status !== "Verified") toast.info(`Your profile is still ${(u.credential_status || "pending").toLowerCase()} verification.`);
  };

  const signOut = () => { logout(); nav("/doctor/login"); };

  return (
    <div className="min-h-screen" data-testid="doctor-onboarding-page"
      style={{ background: "radial-gradient(circle at 12% 15%, rgba(42,82,152,.14), transparent 45%), radial-gradient(circle at 88% 85%, rgba(218,165,32,.12), transparent 45%), linear-gradient(180deg,#f6f8fc,#eef2fa)" }}>
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white/70 backdrop-blur">
        <Logo height={34} />
        <Btn size="sm" onClick={signOut} data-testid="onboarding-signout"><LogOut size={14} /> Sign out</Btn>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-[13px] font-medium mb-5" data-testid="onboarding-pending-banner">
          <Clock size={16} className="shrink-0" />
          <span className="flex-1">Upload your documents within 7 days. Missing documents will remain pending. We&rsquo;ll email you once your profile is verified and activated.</span>
          <Btn size="sm" onClick={check} disabled={checking} data-testid="onboarding-check-status"><RefreshCw size={13} className={checking ? "animate-spin" : ""} /> Check status</Btn>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-slate-200" style={{ boxShadow: "0 20px 60px rgba(10,27,51,.10)" }}>
          <ProDocuments
            userId={user.id}
            credentialStatus={user.credential_status}
            role="doctor"
            subtitle="Upload your medical credentials so PranIQ Admin can verify you. Your doctor portal activates after manual verification."
            onDone={user.credential_status === "Verified" ? () => nav("/dashboard") : undefined}
          />
        </div>
      </main>
    </div>
  );
}
